/**
 * Theme Toggle Component
 * Enhanced theme toggle button with accessibility and better UX
 */
import React from 'react';
import { useTheme } from '../context/ThemeContext';

const ThemeToggle = ({ className = '', size = 'medium', showLabel = false, ...props }) => {
  const { theme, toggleTheme, isLoading, error } = useTheme();

  const isDark = theme === 'dark';

  // Keyboard shortcut support
  React.useEffect(() => {
    const handleKeyboardShortcut = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'T') {
        e.preventDefault();
        // Trigger theme toggle if not loading
        if (!isLoading) {
          toggleTheme();
        }
      }
    };

    document.addEventListener('keydown', handleKeyboardShortcut);
    return () => document.removeEventListener('keydown', handleKeyboardShortcut);
  }, [toggleTheme, isLoading]);

  // Size variants
  const sizeClasses = {
    small: 'text-sm p-1',
    medium: 'text-base p-2',
    large: 'text-lg p-3'
  };

  // Error indicator
  const hasError = error && !isLoading;

  const buttonClasses = `
    dark-mode-toggle
    ${sizeClasses[size]}
    ${className}
    ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
    ${hasError ? 'border-2 border-red-500' : ''}
    border-none
    background-none
    transition-all
    duration-200
    hover:scale-110
    focus:outline-none
    focus:ring-2
    focus:ring-blue-500
    focus:ring-offset-2
    rounded
  `.trim().replace(/\s+/g, ' ');

  const handleClick = (e) => {
    e.preventDefault();
    if (!isLoading) {
      toggleTheme();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e);
    }
  };

  // Icons with better accessibility
  const lightIcon = '☀️';
  const darkIcon = '🌙';
  const loadingIcon = '⏳';

  const icon = isLoading ? loadingIcon : (isDark ? darkIcon : lightIcon);
  const label = isLoading ? 'Switching theme...' : `Switch to ${isDark ? 'light' : 'dark'} mode`;

  return (
    <div className="theme-toggle-container">
      <button
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={buttonClasses}
        aria-label={label}
        title={label}
        disabled={isLoading}
        type="button"
        {...props}
      >
        <span role="img" aria-hidden="true">
          {icon}
        </span>
        {showLabel && (
          <span className="ml-2 text-sm font-medium">
            {isDark ? 'Dark' : 'Light'}
          </span>
        )}
      </button>

      {/* Error tooltip */}
      {hasError && (
        <div
          className="theme-toggle-error absolute bg-red-100 text-red-800 text-xs px-2 py-1 rounded shadow-lg"
          style={{
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginTop: '4px',
            zIndex: 1000,
            whiteSpace: 'nowrap'
          }}
        >
          Theme sync failed - changes saved locally
        </div>
      )}

    </div>
  );
};

export default ThemeToggle;